import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../utils/axios';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  FunnelIcon,
  EyeIcon,
  SparklesIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AgentOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companySearchText, setCompanySearchText] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [pagination.page, statusFilter, companyFilter, searchQuery]);

  const fetchCompanies = async () => {
    try {
      // Try field-agent endpoint first (for agents)
      let response;
      try {
        response = await axios.get('/api/field-agent/companies?limit=1000');
        console.log('✅ Field agent companies:', response.data);
        console.log('📋 First company structure:', response.data.companies[0]);
        setCompanies(response.data.companies || []);
      } catch (agentError) {
        console.log('⚠️ Field agent endpoint failed:', agentError.response?.status, agentError.message);
        // If field-agent endpoint fails (user is admin), fetch all companies
        if (agentError.response?.status === 403 || agentError.response?.status === 401) {
          response = await axios.get('/api/companies?limit=1000&status=active');
          console.log('✅ All companies response:', response.data);
          const companiesList = response.data.companies || response.data.data || [];
          console.log('📋 Companies to set:', companiesList.length, 'companies');
          setCompanies(companiesList);
        } else {
          throw agentError;
        }
      }
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      // Set empty array to prevent UI issues
      setCompanies([]);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      if (statusFilter) params.append('status', statusFilter);
      if (companyFilter) params.append('company_id', companyFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`/api/field-agent/orders?${params}`);
      setOrders(response.data.orders || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('שגיאה בטעינת ההזמנות');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, notes = '') => {
    try {
      setUpdatingStatus(orderId);
      const response = await axios.put(`/api/field-agent/orders/${orderId}/status`, {
        status: newStatus,
        notes
      });
      
      toast.success('סטטוס ההזמנה עודכן בהצלחה');
      
      // Update local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus }
            : order
        )
      );
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס ההזמנה');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'תאריך לא זמין';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'תאריך לא תקין';
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'שגיאה בתאריך';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getStatusBadge = (status, orderId, isEditable = true) => {
    const statusMap = {
      pending: { 
        text: 'ממתין', 
        className: 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300',
        icon: ClockIcon
      },
      processing: { 
        text: 'בטיפול', 
        className: 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300',
        icon: SparklesIcon
      },
      shipped: { 
        text: 'נשלח', 
        className: 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300',
        icon: TruckIcon
      },
      delivered: { 
        text: 'נמסר', 
        className: 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300',
        icon: CheckCircleIcon
      },
      cancelled: { 
        text: 'בוטל', 
        className: 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300',
        icon: XCircleIcon
      }
    };

    const badge = statusMap[status] || { 
      text: status, 
      className: 'bg-gray-100 text-gray-800',
      icon: ShoppingBagIcon
    };

    const Icon = badge.icon;
    const isDisabled = status === 'delivered' || status === 'cancelled';
    const isEditing = editingStatus === orderId;
    const isLoading = updatingStatus === orderId;

    if (isLoading) {
      return (
        <div className="relative inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600 shadow-sm">
          <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-300 border-t-gray-600"></div>
          <span>מעדכן...</span>
        </div>
      );
    }

    if (isEditing && isEditable && !isDisabled) {
      return (
        <div className="relative">
          <select
            autoFocus
            value={status}
            onChange={(e) => {
              const newStatus = e.target.value;
              if (newStatus !== status) {
                if (window.confirm(`האם אתה בטוח שברצונך לשנות את סטטוס ההזמנה ל-${getStatusLabel(newStatus)}?`)) {
                  handleStatusUpdate(orderId, newStatus);
                  setEditingStatus(null);
                } else {
                  setEditingStatus(null);
                }
              } else {
                setEditingStatus(null);
              }
            }}
            onBlur={() => setEditingStatus(null)}
            className={`px-3 py-1 rounded-lg text-xs font-bold ${badge.className} shadow-md border-2 border-primary-500 cursor-pointer outline-none focus:ring-2 focus:ring-primary-300`}
            style={{ appearance: 'auto' }}
          >
            <option value="pending">ממתין</option>
            <option value="processing">בטיפול</option>
            <option value="shipped">נשלח</option>
            <option value="delivered">נמסר</option>
            <option value="cancelled">בוטל</option>
          </select>
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() => {
          if (isEditable && !isDisabled) {
            setEditingStatus(orderId);
          }
        }}
        disabled={isDisabled || !isEditable}
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${badge.className} shadow-sm transition-all ${
          isEditable && !isDisabled 
            ? 'hover:shadow-md hover:scale-105 cursor-pointer' 
            : 'cursor-default opacity-75'
        }`}
        title={isDisabled ? 'לא ניתן לערוך סטטוס זה' : 'לחץ לערוך סטטוס'}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{badge.text}</span>
        {isEditable && !isDisabled && (
          <PencilIcon className="h-3 w-3 ml-1 opacity-60" aria-hidden="true" />
        )}
      </button>
    );
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: 'ממתין',
      processing: 'בטיפול',
      shipped: 'נשלח',
      delivered: 'נמסר',
      cancelled: 'בוטל'
    };
    return statusMap[status] || status;
  };

  // Calculate quick stats
  const stats = useMemo(() => {
    const total = orders.length;
    const totalAmount = orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
    const pending = orders.filter(o => o.status === 'pending').length;
    const delivered = orders.filter(o => o.status === 'delivered').length;

    return { total, totalAmount, pending, delivered };
  }, [orders]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-4 sm:py-6 lg:py-8">
      <div className="w-full mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 space-y-4 sm:space-y-6">
        {/* Modern Header */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-primary-500 to-blue-600 rounded-lg sm:rounded-xl shadow-lg">
                <ShoppingBagIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
                  ההזמנות שלי
                </h1>
                <p className="mt-1 text-xs sm:text-sm text-gray-600">
                  רשימת ההזמנות שביצעת
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mt-4 sm:mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                <div>
                  <p className="text-xs text-blue-700 font-medium">סה"כ הזמנות</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-3">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" aria-hidden="true" />
                <div>
                  <p className="text-xs text-green-700 font-medium">סה"כ ₪</p>
                  <p className="text-lg font-bold text-green-900" dir="ltr">
                    {stats.totalAmount.toFixed(0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
              <div className="flex items-center gap-3">
                <ClockIcon className="h-8 w-8 text-yellow-600" aria-hidden="true" />
                <div>
                  <p className="text-xs text-yellow-700 font-medium">ממתין</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-8 w-8 text-purple-600" aria-hidden="true" />
                <div>
                  <p className="text-xs text-purple-700 font-medium">הושלמו</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.delivered}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Search Bar with Autocomplete */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 z-10" aria-hidden="true" />
              <input
                type="text"
                placeholder="חיפוש לפי מספר הזמנה או שם חברה..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchDropdown(true);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                onFocus={() => setShowSearchDropdown(true)}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                aria-label="חיפוש הזמנות"
                className="w-full pr-8 sm:pr-10 pl-3 sm:pl-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-xs sm:text-sm"
              />
              {showSearchDropdown && searchQuery && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                  {companies
                    .filter(item => {
                      const c = item.company || item;
                      return c.name.toLowerCase().includes(searchQuery.toLowerCase());
                    })
                    .slice(0, 50)
                    .map(item => {
                      const company = item.company || item;
                      return (
                        <button
                          key={company.id}
                          type="button"
                          onClick={() => {
                            setSearchQuery(company.name);
                            setShowSearchDropdown(false);
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                          className="w-full px-3 sm:px-4 py-2 text-right text-xs sm:text-sm hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{company.name}</div>
                          {company.business_number && (
                            <div className="text-xs text-gray-500">ח.פ: {company.business_number}</div>
                          )}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-2 sm:gap-3 w-full">
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
              <label htmlFor="status-filter" className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap flex-shrink-0">
                סטטוס:
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                aria-label="סינון הזמנות לפי סטטוס"
                className="flex-1 min-w-0 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              >
                <option value="">כל הסטטוסים</option>
                <option value="pending">ממתין</option>
                <option value="processing">בטיפול</option>
                <option value="shipped">נשלח</option>
                <option value="delivered">נמסר</option>
                <option value="cancelled">בוטל</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 w-full relative">
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
              <label htmlFor="company-filter-input" className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap flex-shrink-0">
                חברה:
              </label>
              <div className="flex-1 relative">
                <input
                  id="company-filter-input"
                  type="text"
                  placeholder="הקלד שם חברה..."
                  value={companySearchText}
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    setCompanySearchText(searchValue);
                    setShowCompanyDropdown(true);
                    
                    // Clear filter if input is empty
                    if (!searchValue) {
                      setCompanyFilter('');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }
                  }}
                  onFocus={() => setShowCompanyDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                  aria-label="סינון הזמנות לפי חברה"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
                {showCompanyDropdown && companySearchText && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                    {companies
                      .filter(item => {
                        const c = item.company || item;
                        return c.name.toLowerCase().includes(companySearchText.toLowerCase());
                      })
                      .slice(0, 50)
                      .map(item => {
                        const company = item.company || item;
                        return (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => {
                              setCompanyFilter(company.id);
                              setCompanySearchText(company.name);
                              setShowCompanyDropdown(false);
                              setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                            className="w-full px-3 py-2 text-right text-xs sm:text-sm hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{company.name}</div>
                            {company.business_number && (
                              <div className="text-xs text-gray-500">ח.פ: {company.business_number}</div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
              {companyFilter && (
                <button
                  onClick={() => {
                    setCompanyFilter('');
                    setCompanySearchText('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-700"
                  aria-label="נקה סינון חברה"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Active Filters Display & Clear */}
            {(searchQuery || statusFilter || companyFilter) && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 flex-wrap">
                <span className="text-xs text-gray-600 font-medium">פילטרים פעילים:</span>
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
                    חיפוש: {searchQuery}
                    <button
                      onClick={() => setSearchQuery('')}
                      className="hover:text-blue-900"
                      aria-label="נקה חיפוש"
                    >
                      ×
                    </button>
                  </span>
                )}
                {companyFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-md text-xs">
                    חברה: {(() => {
                      const found = companies.find(item => {
                        const c = item.company || item;
                        return c.id === companyFilter;
                      });
                      return found ? (found.company || found).name : '';
                    })()}
                    <button
                      onClick={() => setCompanyFilter('')}
                      className="hover:text-purple-900"
                      aria-label="נקה סינון חברה"
                    >
                      ×
                    </button>
                  </span>
                )}
                {statusFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs">
                    סטטוס: {statusFilter}
                    <button
                      onClick={() => setStatusFilter('')}
                      className="hover:text-green-900"
                      aria-label="נקה סינון סטטוס"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('');
                    setCompanyFilter('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="mr-auto text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  נקה הכל
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">טוען הזמנות...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border-2 border-dashed border-gray-300">
            <ShoppingBagIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">אין הזמנות</h3>
            <p className="text-gray-500 mb-6">
              {(statusFilter || companyFilter || searchQuery) 
                ? 'לא נמצאו הזמנות התואמות את הסינון' 
                : 'אין הזמנות להצגה'}
            </p>
            {(statusFilter || companyFilter || searchQuery) && (
              <button
                onClick={() => {
                  setStatusFilter('');
                  setCompanyFilter('');
                  setSearchQuery('');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                נקה כל הסינונים
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="w-full">
                <table className="w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th scope="col" className="w-[15%] px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        מספר הזמנה
                      </th>
                      <th scope="col" className="w-[20%] px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        חברה
                      </th>
                      <th scope="col" className="w-[15%] px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        תאריך
                      </th>
                      <th scope="col" className="w-[12%] px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        סכום
                      </th>
                      <th scope="col" className="w-[18%] px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        סטטוס
                      </th>
                      <th scope="col" className="w-[20%] px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        פעולות
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order, index) => (
                      <tr 
                        key={order.id} 
                        className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent transition-all duration-200"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <td className="px-3 py-3">
                          <div className="text-xs font-bold text-gray-900 truncate" title={order.order_number}>
                            #{order.order_number}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 bg-gradient-to-br from-primary-100 to-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-primary-700">
                                {(order.company?.name || '?')[0].toUpperCase()}
                              </span>
                            </div>
                            <span className="text-xs font-medium text-gray-900 truncate" title={order.company?.name || 'לא ידוע'}>
                              {order.company?.name || 'לא ידוע'}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-xs text-gray-600">
                            {formatDate(order.created_at)}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-xs font-bold text-primary-600">
                            {formatCurrency(order.total_amount)}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end">
                            {getStatusBadge(order.status, order.id)}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end">
                            <Link
                              to={`/agent/orders/${order.id}`}
                              aria-label={`צפה בהזמנה ${order.order_number}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 font-medium text-xs"
                            >
                              <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              <span>צפה</span>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tablet/Mobile Cards */}
            <div className="lg:hidden space-y-3 sm:space-y-4">
              {orders.map((order, index) => (
                <div
                  key={order.id}
                  className="bg-white rounded-lg sm:rounded-xl shadow-md sm:shadow-lg border border-gray-200 p-3 sm:p-4 hover:shadow-lg sm:hover:shadow-xl transition-all duration-200"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-start justify-between mb-2 sm:mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1">מספר הזמנה</p>
                      <p className="text-sm sm:text-base font-bold text-gray-900 truncate" title={order.order_number}>
                        #{order.order_number}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0 mr-2">
                      {getStatusBadge(order.status, order.id)}
                    </div>
                  </div>

                  <div className="space-y-2 mb-3 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-primary-100 to-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary-700">
                          {(order.company?.name || '?')[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate" title={order.company?.name || 'לא ידוע'}>
                          {order.company?.name || 'לא ידוע'}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">סכום</p>
                      <p className="text-sm sm:text-base font-bold text-primary-600">
                        {formatCurrency(order.total_amount)}
                      </p>
                    </div>
                    <Link
                      to={`/agent/orders/${order.id}`}
                      aria-label={`צפה בהזמנה ${order.order_number}`}
                      className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium text-xs sm:text-sm flex-shrink-0"
                    >
                      <EyeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                      <span>צפה</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      aria-label="עמוד קודם"
                      className="relative inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      הקודם
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.pages}
                      aria-label="עמוד הבא"
                      className="mr-3 relative inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      הבא
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        מציג{' '}
                        <span className="font-bold text-primary-600">{(pagination.page - 1) * pagination.limit + 1}</span>
                        {' '}עד{' '}
                        <span className="font-bold text-primary-600">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>
                        {' '}מתוך{' '}
                        <span className="font-bold text-primary-600">{pagination.total}</span>
                        {' '}תוצאות
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-lg shadow-sm gap-2" aria-label="Pagination">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                          disabled={pagination.page === 1}
                          aria-label="עמוד קודם"
                          className="relative inline-flex items-center px-4 py-2 border-2 border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          הקודם
                        </button>
                        <span className="relative inline-flex items-center px-6 py-2 border-2 border-primary-300 bg-gradient-to-r from-primary-50 to-blue-50 text-sm font-bold text-primary-700 rounded-lg">
                          {pagination.page} / {pagination.pages}
                        </span>
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                          disabled={pagination.page === pagination.pages}
                          aria-label="עמוד הבא"
                          className="relative inline-flex items-center px-4 py-2 border-2 border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          הבא
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentOrdersPage;
